import { useEffect, useState } from "react";
import { AlertTriangle, BellRing, Check, Copy, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateIt, money } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Solleciti = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [minDays, setMinDays] = useState(30);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState(null);
  const [sending, setSending] = useState(false);
  const [reminderTo, setReminderTo] = useState("");

  const sendReminder = async () => {
    setSending(true);
    try {
      const { data: res } = await api.post(`/reminders/${draft.id}/send`, {
        subject: draft.subject,
        body: draft.body,
        attach_pdf: true,
      });
      toast.success(`${t("mail.sent")} ${res.to}`);
      setDraft(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("mail.notConfigured"));
    } finally {
      setSending(false);
    }
  };

  const load = () =>
    api
      .get("/reminders", { params: { min_days: minDays } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t("rem.failed")));

  useEffect(() => {
    load();
  }, [minDays]);

  const generate = async (doc) => {
    setBusy(doc.id);
    try {
      const { data } = await api.post(`/ai/reminder/${doc.id}`);
      setDraft({ ...data, id: data.id || doc.id });
      setReminderTo(data.client?.email || doc.client?.email || "");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("rem.failed"));
    } finally {
      setBusy(null);
    }
  };

  const markPaid = async (doc) => {
    try {
      await api.patch(`/documents/${doc.id}/status`, { status: "pagata" });
      toast.success(`${doc.number} — ${t("rem.paidToast")}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || t("rem.failed"));
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`);
      toast.success(t("rem.copied"));
    } catch {
      toast.error(t("rem.copyFailed"));
    }
  };

  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  // Somma separata per valuta: le fatture scadute possono essere in valute diverse.
  const overdueTotals =
    Object.entries(
      (data.items || []).reduce((acc, d) => {
        const key = d.currency || "EUR";
        acc[key] = (acc[key] || 0) + d.total;
        return acc;
      }, {})
    )
      .map(([currency, sum]) => money(sum, currency))
      .join(" · ") || money(0);

  return (
    <div className="space-y-5" data-testid="reminders-page">
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card-flat relative overflow-hidden bg-slate-950 p-6 text-white lg:col-span-2 grain">
          <p className="relative flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-300">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("rem.toRecover")}
          </p>
          <p className="font-mono-num relative mt-3 text-4xl font-semibold" data-testid="overdue-amount">
            {overdueTotals}
          </p>
          <p className="relative mt-2 text-sm text-slate-400">
            {data.count} {data.count === 1 ? t("rem.overdueOne") : t("rem.overdueMany")} {minDays}{" "}
            {t("rem.days")}
          </p>
        </div>
        <div className="card-flat p-6">
          <Label htmlFor="min-days">{t("rem.threshold")}</Label>
          <Input
            id="min-days"
            data-testid="min-days"
            type="number"
            value={minDays}
            onChange={(e) => setMinDays(Number(e.target.value) || 0)}
            className="font-mono-num mt-1.5 focus:ring-2 focus:ring-blue-600"
          />
          <p className="mt-3 text-xs text-slate-500">{t("rem.toneHint")}</p>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">{t("common.number")}</th>
              <th className="px-4 py-3">{t("common.client")}</th>
              <th className="px-4 py-3">{t("common.due")}</th>
              <th className="px-4 py-3 text-right">{t("rem.delay")}</th>
              <th className="px-4 py-3 text-right">{t("common.amount")}</th>
              <th className="px-4 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="reminders-table">
            {data.items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                  {t("rem.empty")}
                </td>
              </tr>
            )}
            {data.items.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50">
                <td className="font-mono-num px-4 py-3 font-medium">{d.number}</td>
                <td className="px-4 py-3">{d.client?.name}</td>
                <td className="font-mono-num px-4 py-3 text-slate-600">
                  {dateIt(d.due_date || d.issue_date)}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="rounded bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
                    {d.days_overdue} {t("rem.days")}
                  </span>
                </td>
                <td className="font-mono-num px-4 py-3 text-right font-semibold">{money(d.total, d.currency)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <Button
                      data-testid={`generate-reminder-${d.number}`}
                      onClick={() => generate(d)}
                      disabled={busy === d.id}
                      className="h-8 gap-1.5 bg-indigo-600 px-3 text-xs text-white hover:bg-indigo-500"
                    >
                      {busy === d.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <BellRing className="h-3.5 w-3.5" />
                      )}
                      {t("rem.aiReminder")}
                    </Button>
                    <Button
                      data-testid={`mark-paid-${d.number}`}
                      variant="outline"
                      onClick={() => markPaid(d)}
                      className="h-8 gap-1.5 px-3 text-xs"
                    >
                      <Check className="h-3.5 w-3.5" /> {t("rem.markPaid")}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!draft} onOpenChange={() => setDraft(null)}>
        <DialogContent className="max-w-xl" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle>{t("rem.dialogTitle")} {draft?.number}</DialogTitle>
            <DialogDescription>
              {draft?.client?.name} · {money(draft?.total || 0, draft?.currency)} · {draft?.days_overdue}{" "}
              {t("rem.daysLate")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("mail.to")}</Label>
              <Input
                data-testid="reminder-to"
                value={reminderTo}
                onChange={(e) => setReminderTo(e.target.value)}
                placeholder="cliente@email.it"
                className="mt-1.5 focus:ring-2 focus:ring-blue-600"
              />
            </div>
            <div>
              <Label>{t("rem.subject")}</Label>
              <Input data-testid="reminder-subject" readOnly value={draft?.subject || ""} className="mt-1.5" />
            </div>
            <div>
              <Label>{t("rem.body")}</Label>
              <Textarea
                data-testid="reminder-body"
                readOnly
                rows={10}
                value={draft?.body || ""}
                className="mt-1.5 text-sm"
              />
            </div>
            <Button
              data-testid="send-reminder-email"
              onClick={sendReminder}
              disabled={sending}
              className="w-full gap-2 bg-indigo-600 text-white hover:bg-indigo-500"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {t("mail.sendReminder")}
            </Button>
            <Button
              data-testid="copy-reminder"
              variant="outline"
              onClick={copy}
              className="w-full gap-2"
            >
              <Copy className="h-4 w-4" /> {t("rem.copy")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Solleciti;
